import { zeroAddress } from 'viem'
import { dolomiteEmode } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { LenderCrossPoolMeta, UserData } from '../utils/types'
import { createMultiAccountTypeUserState } from '../utils/createMultiAccountStyleUserState'
import { resolveDolomiteMode } from '../../public-data/dolomite/emodeConfig'
import { getResolvedDolomiteSubAccounts } from './userCallBuild'

// Indexes into the `getAccountBalances` result tuple
// → [uint256[] markets, address[] tokens, Par[] pars, Wei[] weis]
enum DolomiteAccountBalancesIndexes {
  markets = 0,
  tokens = 1,
  pars = 2,
  weis = 3,
}

/**
 * Returns a [converter, expectedCallCount] tuple for Dolomite user data.
 *
 * Each raw result is one sub-account's `getAccountBalances` call. The set of
 * sub-accounts `(owner, number)` — the owner being the user's isolation-mode
 * VAULT for dGM/dsavETH/… positions — and therefore the call order is
 * resolved by the matching `buildDolomiteUserCall` and read back here from
 * the build-phase store.
 *
 * Dolomite is cross-margin: every supplied market in an account backs that
 * account's debt, so positions emit with `collateralEnabled: true` and the
 * single synthetic mode `'0'`. Collateral factors (and any e-mode override)
 * live in the public-data meta `configs` and are applied by the helper.
 */
export const getDolomiteUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  metaMap?: LenderCrossPoolMeta,
): [(data: any[]) => UserData | undefined, number] => {
  const subAccounts = getResolvedDolomiteSubAccounts(chainId, account) ?? [
    { id: '0', owner: account, number: '0' },
  ]
  const expectedNumberOfCalls = subAccounts.length
  const emode = dolomiteEmode()?.[chainId]

  return [
    (_data: any[]) => {
      if (_data.length !== expectedNumberOfCalls) return undefined

      const histData: {
        [id: string]: { totalDebt24h: number; totalDeposits24h: number }
      } = {}
      const lendingPositions: any = {}
      const modes: { [id: string]: string } = {}

      _data.forEach((res: any, i: number) => {
        // A failed read is not an empty account — skip it instead of emitting a
        // sub-account with no balances.
        if (isFailedCall(res)) return

        // Position id: the account number for the user's own accounts,
        // `iso:<marketId>:<number>` for a vault-owned isolation position —
        // the pair the Dolomite routers address it by.
        const id = subAccounts[i].id
        histData[id] = { totalDebt24h: 0, totalDeposits24h: 0 }
        lendingPositions[id] = {}

        const marketIds: any[] =
          res?.[DolomiteAccountBalancesIndexes.markets] ?? []
        const tokens: string[] =
          res?.[DolomiteAccountBalancesIndexes.tokens] ?? []
        const weis: { sign: boolean; value: bigint }[] =
          res?.[DolomiteAccountBalancesIndexes.weis] ?? []
        const pars: { sign: boolean; value: bigint }[] =
          res?.[DolomiteAccountBalancesIndexes.pars] ?? []

        // Dolomite e-mode (segregated risk): the account's category is derived
        // from the assets it holds — all-same-category → that category's tighter
        // ratio (premiums ignored), else base `'0'`. The helper then applies
        // `configs[mode]` per market (public-data emits a config per category).
        modes[id] = resolveDolomiteMode(
          marketIds.map((m) => String(m)),
          emode,
          subAccounts[i].isolationMarketId,
        )

        tokens.forEach((token: string, k: number) => {
          const wei = weis[k]
          if (!wei) return
          // Dolomite uses real WETH on-chain; normalize to zeroAddress so token-list
          // lookups / market UIDs match the rest of the stack only if the meta keys
          // native that way. Default: keep the raw lowercased token address.
          const underlying = normalizeUnderlying(token)
          // uid keyed by Dolomite marketId (parallel to `tokens`), matching the
          // public-data converters so `metaMap[key]` joins correctly.
          const key = createMarketUid(chainId, lender, String(marketIds[k]))
          const meta = metaMap?.[key]
          if (!meta) return

          const decimals = meta.asset?.decimals ?? 18
          const amount = parseRawAmount(wei.value, decimals)
          const isSupply = wei.sign // true = supplied, false = borrowed
          const deposits = isSupply ? amount : '0'
          const debt = isSupply ? '0' : amount
          // Raw par magnitude of the debt (index-scaled principal — stable as
          // interest accrues). Needed for dust-free native repay-all via the
          // router's `depositParPayable`. Supply rows carry '0'.
          const debtPar = isSupply ? '0' : (pars[k]?.value?.toString() ?? '0')

          const price = getDisplayPrice(meta)
          const oPrice = getOraclePrice(meta)
          const priceHist = meta?.price?.priceUsd24h ?? price

          histData[id].totalDeposits24h += Number(deposits) * priceHist
          histData[id].totalDebt24h += Number(debt) * priceHist

          lendingPositions[id][key] = {
            marketUid: key,
            underlying,
            deposits,
            debt,
            debtPar,
            debtStable: '0',
            debtStableUSD: 0,
            depositsUSD: price * Number(deposits),
            debtUSD: price * Number(debt),
            depositsUSDOracle: oPrice * Number(deposits),
            debtStableUSDOracle: 0,
            debtUSDOracle: oPrice * Number(debt),
            collateralEnabled: true,
          }
        })
      })

      const payload = {
        chainId,
        lendingPositions,
        modes,
        account,
      }

      return createMultiAccountTypeUserState(payload, metaMap!, histData)
    },
    expectedNumberOfCalls,
  ]
}

const WETH_BY_CHAIN: Record<string, string> = {
  // populated by the meta's native handling; kept as a hook. Default: identity.
}

function normalizeUnderlying(token: string): string {
  const lower = token.toLowerCase()
  // If a chain maps its WETH to native, swap here. Until WETH_BY_CHAIN is
  // populated this is identity — the meta keys Dolomite markets by WETH address.
  return Object.values(WETH_BY_CHAIN).includes(lower) ? zeroAddress : lower
}
